import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  BaseFilter,
  ListResponseOfKnowledgeLevel,
} from "services/KnowfullyAdminClient";
import { IdValue } from "types/common-types";

type SelectKnowledgeLevelProps = {
  value?: string | null;
  onChange: (id: string | null) => void;
  onDirty?: () => void;
  onClean?: () => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<IdValue>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectKnowledgeLevel = ({
  value: knowledgeLevelId,
  onChange,
  onDirty,
  onClean,
  ...rest
}: SelectKnowledgeLevelProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [knowledgeLevel, setKnowledgeLevel] = useState<IdValue | null>(null);

  // Load the initial value if we're passed a knowledgeLevelId
  const { isLoading, data } = useQuery<ListResponseOfKnowledgeLevel>(
    ["knowledgeLevelById", knowledgeLevelId],
    () =>
      skuService.getKnowledgeLevels(
        new AdminListRequestOfBaseFilter({ sortField: "value" })
      ),
    {
      enabled:
        !!knowledgeLevelId &&
        (!knowledgeLevel || knowledgeLevel.id !== knowledgeLevelId),
    }
  );

  useEffect(() => {
    // Once the initial value loads, update the value (if is hasn't been set by a search)
    if (data?.data?.[0]) {
      setKnowledgeLevel({
        id: data!.data![0].id,
        value: data!.data![0].value ?? "?",
      });
    }
  }, [data, knowledgeLevel]);

  const fetchKnowledgeLevels = async (searchTerm: string) => {
    const result = await queryClient.fetchQuery<ListResponseOfKnowledgeLevel>(
      ["knowledgeLevels", searchTerm],
      () =>
        skuService.getKnowledgeLevels(
          new AdminListRequestOfBaseFilter({
            pageNumber: 0,
            pageSize: 20,
            parameters: BaseFilter.fromJS({
              searchText: searchTerm === "" ? undefined : searchTerm,
            }),
            sortField: "value",
          })
        )
    );
    // We need to massage these results into the standard format

    const levels = {
      ...result,
      data: (result.data ?? []).map(({ id, value }) => ({
        id,
        value: value ?? "?",
      })),
    };

    return { result: levels, forSearchTerm: searchTerm };
  };

  const onChangeKnowledgeLevel = (knowledgeLevel: IdValue | null) => {
    setKnowledgeLevel(knowledgeLevel);
    onChange(knowledgeLevel?.id ?? null);
    if (knowledgeLevel !== null) {
      onDirty && onDirty();
    } else {
      onClean && onClean();
    }
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={knowledgeLevel}
      isLoadingInitialValue={isLoading}
      labelField="value"
      idField="id"
      fetchData={fetchKnowledgeLevels}
      failMessage={t({ id: "selectProductFetchFailed" })}
      onChange={onChangeKnowledgeLevel}
    />
  );
};

export default SelectKnowledgeLevel;
